import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { SyncOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Popover, Stack, Tooltip, Typography } from '@lyearn/ui';
import { ContextualUserGroupType } from '@/components/Form/components/VisibilitySettings/types';
import { DEFAULT_GROUP_ITEM_MODE, GroupItemMode } from '@/modules/corporate/modules/group/types';
import { getGroupImage } from '@/modules/corporate/modules/group/views/GroupManager/utils';
import { GroupTypeEnum } from '@/types/schema';

import { ContentCollectionFragment } from '../../views/GroupManager/graphql/fragments/generated/ContentCollection';
import { UserCollectionFragment } from '../../views/GroupManager/graphql/fragments/generated/UserCollection';
import { GROUP_IMAGE_CLASSES } from '../../views/GroupManager/utils/consts';
import { GROUP_TAB_CLASSES } from './consts';
import GroupMemberList from './GroupMemberList';

export interface GroupTabProps {
  group: (UserCollectionFragment | ContentCollectionFragment | ContextualUserGroupType) & {
    descriptionNode?: React.ReactNode;
  };
  mode?: GroupItemMode;
  showAutoSync?: boolean;
}

function GroupTab(props: GroupTabProps) {
  const { t } = useTranslation('Group');
  const { group, mode = DEFAULT_GROUP_ITEM_MODE, showAutoSync = false } = props;
  const classes = GROUP_TAB_CLASSES[mode];

  const [showPopover, { toggle: togglePopover }] = useToggle(false);

  if (group.type === 'ContextualUserGroupType') {
    const { Icon, label, descriptionNode } = group;
    const {
      userCollection: { root },
    } = GROUP_IMAGE_CLASSES[mode];

    return (
      <Stack alignItems="center" className={classes.root}>
        <Stack
          alignItems="center"
          className={cn('relative flex-shrink-0', root)}
          justifyContent="center">
          <Icon fillColorToken="icon-active" height={40} width={40} />
        </Stack>
        <Stack className={classes.container} direction="col">
          <Stack className="truncate">
            <Typography
              className="truncate"
              color="text-text-primary"
              title={group.id}
              {...classes.name}>
              {label}
            </Typography>
          </Stack>
          {descriptionNode}
          {/*TODO needed backend api*/}
          {/*<Typography color="text-text-secondary" variant="body-short-02" {...classes.description}>*/}
          {/*  {t('Common:Departments.Member', { count: 0 })}*/}
          {/*</Typography>*/}
        </Stack>
      </Stack>
    );
  }

  const Image = getGroupImage({ group, type: GroupTypeEnum.UserCollection, mode, t });
  /*@ts-ignore*/
  const count = group?.usersCount;

  return (
    <Stack alignItems="center" className={classes.root}>
      {Image}
      <Stack className={classes.container} direction="col">
        <Stack className="truncate">
          <Typography
            className="truncate"
            color="text-text-primary"
            title={group.name}
            {...classes.name}>
            {group.name}
          </Typography>
          {showAutoSync && (
            <Tooltip title={`${t('Group:Manager.AutoSyncWithCondition')}`}>
              <Box role="tooltip" tabIndex={0}>
                <SyncOutline
                  className="mt-4 flex-shrink-0 text-icon-primary ms-6"
                  height={16}
                  width={16}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>

        {group.descriptionNode ? (
          group.descriptionNode
        ) : (
          <Stack gapX={8}>
            <Typography
              color="text-text-secondary"
              variant="body-short-02"
              {...classes.description}>
              {t('Common:Departments.Member', { count: count })}
            </Typography>
            <Popover open={showPopover} onOpenChange={togglePopover}>
              <Popover.Button>
                <LinkButton
                  className={cn('text-link-internal opacity-0 group-hover/assign:opacity-100', {
                    'opacity-100': showPopover,
                  })}
                  typography={{
                    variant: 'label-02',
                  }}>
                  {t('Common:Actions.View')}
                </LinkButton>
              </Popover.Button>
              <Popover.Content className="!rounded-12" position="right">
                <GroupMemberList count={count} groupId={group.id} title={group.name} />
              </Popover.Content>
            </Popover>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default React.memo(GroupTab);
